import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useAuth, useProtectedPage } from '../../../context/AuthContext'
import callAPI from '../../../helpers/callAPI'

export default function QuickBooks({ showSystemMessage }) {
    document.title = 'QuickBooks'

    const auth = useAuth()
    useProtectedPage(auth.permissions, 'quickbooks')

    const history = useHistory()
    const [status, setStatus] = useState(null)
    const [populateSummary, setPopulateSummary] = useState(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const connected = params.get('connected')
        const error = params.get('error')

        if (connected === '1') {
            showSystemMessage('success', 'QuickBooks connected.')
            history.replace('/admin/quickbooks')
            loadStatus()
        } else if (error) {
            showSystemMessage('error', error)
            history.replace('/admin/quickbooks')
        }
    }, [])

    const loadStatus = async () => {
        setLoading(true)
        const result = await callAPI('quickbooks/status', 'GET')
        setLoading(false)

        if (result.success) {
            setStatus(result)
        } else {
            showSystemMessage('error', result.errors.message)
            setStatus(null)
        }
    }

    const handleConnect = async () => {
        setLoading(true)
        const result = await callAPI('quickbooks/connect', 'GET')
        setLoading(false)

        if (result.success) {
            window.location = result.authorizationUrl
        } else {
            showSystemMessage('error', result.errors.message)
        }
    }

    const handleDownloadCsv = async () => {
        setLoading(true)
        const mbToken = localStorage.getItem('adm-auth-token')
        const response = await fetch(`${process.env.REACT_APP_API_URL}/quickbooks/customers/csv`, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'x-app-token': mbToken,
            },
        })

        if (response.status !== 200) {
            showSystemMessage('error', 'Unable to download CSV.')
            setLoading(false)
            return
        }

        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'qbo-migration.xls'
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
        setLoading(false)
    }

    const handlePopulateMerchantIds = async () => {
        if (!window.confirm('Write mb_merchant_id from zzz_qbo_temp into QuickBooks customer custom field merchant_id?')) {
            return
        }

        setLoading(true)
        setPopulateSummary(null)
        const result = await callAPI('quickbooks/populateMerchantIds', 'POST')
        setLoading(false)

        if (result.success) {
            setPopulateSummary(result)
            showSystemMessage('success', `Updated ${result.summary.updated}/${result.summary.total}`)
        } else {
            showSystemMessage('error', result.errors.message)
        }
    }

    return (
        <div>
            <div className="page-header d-flex justify-content-between align-items-center">
                <span>QuickBooks</span>
            </div>
            <div className="card">
                <div className="card-body">
                    <div className="mb-3">
                        <div className="d-flex align-items-center mb-3">
                            <button className="btn btn-primary" type="button" disabled={loading} onClick={loadStatus} style={{ minWidth: 160 }}>
                                Status
                            </button>
                            <span className="ml-3 text-muted">Check whether QuickBooks is connected, and show realm ID and environment.</span>
                        </div>
                        <div className="d-flex align-items-center mb-3">
                            <button className="btn btn-success" type="button" disabled={loading} onClick={handleConnect} style={{ minWidth: 160 }}>
                                Connect
                            </button>
                            <span className="ml-3 text-muted">Start the OAuth handshake. You will be sent to Intuit to Allow access, then returned here.</span>
                        </div>
                        <div className="d-flex align-items-center mb-3">
                            <button className="btn btn-secondary" type="button" disabled={loading} onClick={handleDownloadCsv} style={{ minWidth: 160 }}>
                                Download CSV
                            </button>
                            <span className="ml-3 text-muted">Download the QBO customers spreadsheet joined to MaxBounty merchant names and IDs.</span>
                        </div>
                        <div className="d-flex align-items-center">
                            <button className="btn btn-warning" type="button" disabled={loading} onClick={handlePopulateMerchantIds} style={{ minWidth: 160 }}>
                                Populate merchant_id
                            </button>
                            <span className="ml-3 text-muted">Write mb_merchant_id from zzz_qbo_temp into the QuickBooks merchant_id custom field.</span>
                        </div>
                    </div>
                    {populateSummary && (
                        <div className="mb-3 bg-light p-3 border rounded">
                            <div>
                                Updated {populateSummary.summary.updated}/{populateSummary.summary.total}
                            </div>
                            <div className="text-muted">
                                skipped {populateSummary.summary.skipped}, invalid {populateSummary.summary.invalid}, conflicts{' '}
                                {populateSummary.summary.conflicts}, errors {populateSummary.summary.errors}
                            </div>
                            <div className="text-muted">
                                environment: {populateSummary.environment}, realmId: {populateSummary.realmId}, targetField:{' '}
                                {populateSummary.targetField}, definitionId: {populateSummary.definitionId || '(none)'}
                            </div>
                            <pre className="mb-0 mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                                {JSON.stringify(
                                    {
                                        customFieldsSeen: populateSummary.customFieldsSeen,
                                        details: populateSummary.details,
                                    },
                                    null,
                                    2
                                )}
                            </pre>
                        </div>
                    )}
                    {status && <pre className="mb-0 bg-light p-3 border rounded">{JSON.stringify(status, null, 2)}</pre>}
                </div>
            </div>
        </div>
    )
}
